import { PlusOutlined } from '@ant-design/icons'

import { Button, PageHeader } from '@lmc/ui-kit'
import useResidentFullName from 'hooks/useResidentFullName'
import { NAMESPACE } from 'locale'
import { deviceRecordConstants } from 'locale/deviceRecords'
import type React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import useAbilityAccess from '../../../../components/AccessControl/useAbilityAccess'
import { DeviceRecordList } from './components/DeviceRecordList'
import DeviceRecordModal from './components/DeviceRecordModal'
import { useDeviceRecordsList } from './hooks/useDeviceRecordsList'
import type { DeviceRecord } from './types'

export const ResidentDeviceRecordsScreen: React.FC = () => {
  const { t } = useTranslation(NAMESPACE.DEVICE_RECORDS)
  const { id: residentId } = useParams<{ id: string }>()
  const residentName = useResidentFullName(residentId)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [selectedDeviceRecord, setSelectedDeviceRecord] = useState<
    DeviceRecord | undefined
  >(undefined)

  const {
    deviceRecords,
    isLoading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useDeviceRecordsList({
    residentId: residentId || '',
    limit: 50
  })

  const hasAddAccess = useAbilityAccess([
    {
      i: 'create',
      this: 'DeviceRecord'
    }
  ])

  const handleAddClick = () => {
    setSelectedDeviceRecord(undefined)
    setIsModalVisible(true)
  }

  const handleEdit = (deviceRecord: DeviceRecord) => {
    setSelectedDeviceRecord(deviceRecord)
    setIsModalVisible(true)
  }

  const handleCloseModal = () => {
    setIsModalVisible(false)
    setSelectedDeviceRecord(undefined)
  }

  return (
    <>
      <PageHeader
        title={
          residentName
            ? t(deviceRecordConstants.DEVICE_RECORDS_FOR, {
                name: residentName
              })
            : t(deviceRecordConstants.DEVICE_RECORDS)
        }
        help={{
          content: t(deviceRecordConstants.DEVICE_RECORDS_TOOLTIP)
        }}
        sticky
      >
        {hasAddAccess && (
          <Button variant="primary" onClick={handleAddClick}>
            <PlusOutlined />
            {t(deviceRecordConstants.ADD_DEVICE_RECORD)}
          </Button>
        )}
      </PageHeader>

      <DeviceRecordList
        deviceRecords={deviceRecords}
        isLoading={isLoading}
        onEdit={handleEdit}
        onRefetch={refetch}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />

      <DeviceRecordModal
        isVisible={isModalVisible}
        onClose={handleCloseModal}
        onSuccess={handleCloseModal}
        deviceRecord={selectedDeviceRecord}
        residentId={residentId || ''}
      />
    </>
  )
}
